import { useEffect, useState } from 'react';
import { SignaturePad } from '../../components/SignaturePad.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { BoutonCorriger } from '../../components/ui/Inputs.jsx';
import { SOC } from '../../constants/company';
import { delaiEnMois } from '../../lib/contrat';
import { printDoc } from '../../lib/print';
import { C, docAcc } from '../../theme/palette';

export function LettreDemission({ emp, soc, onRemettre, dejaRemise }) {
  // Remise de la lettre au patron : figée au serveur, rangée dans le dossier.
  const [remise, setRemise] = useState({ etat: dejaRemise ? 'ok' : 'repos', erreur: '' });
  useEffect(() => {
    if (dejaRemise) setRemise((r) => (r.etat === 'repos' ? { etat: 'ok', erreur: '' } : r));
  }, [dejaRemise]);
  const [step, setStep] = useState('form');
  const [date, setDate] = useState(() => {
    const d = new Date();
    return (
      d.getFullYear() +
      '-' +
      String(d.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(d.getDate()).padStart(2, '0')
    );
  });
  const [dernier, setDernier] = useState('');
  const [motif, setMotif] = useState('');
  const [sign, setSign] = useState('');
  const [delaiMois, setDelaiMois] = useState(() => delaiEnMois(emp));
  const paysCH = !soc?.pays || soc.pays === 'CH';
  const dateCalc = (() => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d)) return '';
    let end;
    if (paysCH) {
      end = new Date(d.getFullYear(), d.getMonth() + delaiMois + 1, 0);
    } else {
      end = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      end.setMonth(end.getMonth() + delaiMois);
    }
    return (
      end.getFullYear() +
      '-' +
      String(end.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(end.getDate()).padStart(2, '0')
    );
  })();
  const remet = async () => {
    if (!onRemettre || remise.etat === 'envoi') return;
    setRemise({ etat: 'envoi', erreur: '' });
    try {
      await onRemettre({
        dateLettre: date,
        dernierJour: dernier || dateCalc || null,
        motif: motif && motif.trim() ? motif.trim() : null,
        signature: sign || emp?.signature || null,
      });
      setRemise({ etat: 'ok', erreur: '' });
    } catch (e) {
      setRemise({ etat: 'echec', erreur: e?.message || 'La lettre n’a pas pu être remise.' });
    }
  };
  const boutonRemise = onRemettre ? (
    <div style={{ marginTop: 9 }}>
      <Btn
        label={
          remise.etat === 'envoi'
            ? '⏳ Remise en cours…'
            : remise.etat === 'ok'
              ? '✓ Lettre remise au patron — la rouvrir'
              : '📤 Remettre ma démission au patron'
        }
        color={remise.etat === 'ok' ? C.green : C.purple}
        full
        disabled={remise.etat === 'envoi' || !dernier || !(sign || emp?.signature)}
        onClick={remet}
      />
      {remise.etat === 'ok' && (
        <div style={{ fontSize: 9, color: C.green, marginTop: 6, textAlign: 'center' }}>
          Elle est dans ton dossier (« Mes documents ») et le patron la lit en PDF.
        </div>
      )}
      {remise.erreur && (
        <div
          role="alert"
          style={{ fontSize: 9.5, color: C.red, marginTop: 6, textAlign: 'center' }}
        >
          {remise.erreur}
        </div>
      )}
    </div>
  ) : null;
  if (!emp)
    return (
      <Card>
        <div style={{ padding: 18, fontSize: 11, color: C.muted }}>Employé introuvable.</div>
      </Card>
    );
  const _da = docAcc(soc),
    accInk = _da.accInk;
  const dd = (iso) => {
    if (!iso) return '____________';
    const z = ('' + iso).split('-');
    return z.length === 3 ? z[2] + '.' + z[1] + '.' + z[0] : iso;
  };
  const dateEmis = new Date().toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const lieu =
    (soc?.npa || SOC.npa || '').split(' ').slice(1).join(' ') || soc?.npa || SOC.npa || '';
  const inp = {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid ' + C.border,
    borderRadius: 8,
    padding: '9px 10px',
    fontSize: 12,
  };

  if (step === 'letter') {
    return (
      <div
        className="doc-root"
        style={{
          height: '100%',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          background: '#4a4e57',
        }}
      >
        <style>
          {
            '.a4-sheet{width:100%;max-width:210mm}@media(max-width:520px){.a4-wrap{padding:10px 6px!important}.a4-sheet{padding:22px 16px!important}}@media print{@page{size:A4;margin:0}html,body{background:#fff!important;margin:0!important;padding:0!important}.no-print{display:none!important}.doc-root{height:auto!important;overflow:visible!important;background:#fff!important}.a4-wrap{margin:0!important;padding:0!important;background:#fff!important}.a4-sheet{box-shadow:none!important;margin:0!important;width:210mm!important;max-width:210mm!important;min-height:auto!important;padding:15mm 15mm!important}}'
          }
        </style>
        <div
          className="no-print"
          style={{
            background: accInk,
            color: '#fff',
            padding: '12px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 5,
          }}
        >
          <button
            onClick={() => setStep('form')}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ‹ Retour
          </button>
          <div style={{ display: 'flex', gap: 7 }}>
            <button
              onClick={printDoc}
              style={{
                background: C.green,
                border: 'none',
                color: '#fff',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              📄 PDF
            </button>
            <button
              onClick={printDoc}
              style={{
                background: 'rgba(255,255,255,.2)',
                border: '1px solid rgba(255,255,255,.45)',
                color: '#fff',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              🖨 Imprimer
            </button>
          </div>
        </div>
        <div
          className="no-print"
          style={{
            background: '#eceef1',
            color: '#555',
            padding: '7px 14px',
            fontSize: 9,
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          📱 iPhone : touche « PDF » → aperçu A4. · 💻 Ordinateur : « Imprimer » → « Enregistrer en
          PDF ».
        </div>
        <div
          className="a4-wrap"
          style={{ padding: '18px 12px', display: 'flex', justifyContent: 'center' }}
        >
          <div
            className="a4-sheet"
            style={{
              width: '100%',
              maxWidth: '210mm',
              minHeight: '297mm',
              background: '#fff',
              color: '#0F1A2E',
              boxSizing: 'border-box',
              padding: '15mm 15mm',
              boxShadow: '0 6px 24px rgba(0,0,0,.35)',
              fontFamily: "'Helvetica Neue',Arial,sans-serif",
            }}
          >
            <div style={{ fontSize: 11.5, lineHeight: 1.6, marginBottom: 2 }}>
              <b>{emp.nom}</b>
            </div>
            {emp.tel && <div style={{ fontSize: 10.5, color: '#5a6270' }}>{emp.tel}</div>}
            {emp.email && <div style={{ fontSize: 10.5, color: '#5a6270' }}>{emp.email}</div>}
            <div style={{ marginTop: 26, textAlign: 'right', fontSize: 11, lineHeight: 1.6 }}>
              <b>{soc?.nom || SOC.nom}</b>
              <br />
              {soc?.adr || SOC.adr}
              <br />
              {soc?.npa || SOC.npa}
            </div>
            <div style={{ marginTop: 22, fontSize: 11, color: '#3a4152' }}>
              {lieu}
              {lieu ? ', le ' : 'Le '}
              {dd(date)}
            </div>
            <div style={{ marginTop: 18, fontSize: 12.5, fontWeight: 800, color: accInk }}>
              Objet : Démission (courrier recommandé)
            </div>
            <div style={{ marginTop: 16, fontSize: 11.5, lineHeight: 1.75, color: '#2a3140' }}>
              <p style={{ margin: '0 0 12px' }}>Madame, Monsieur,</p>
              <p style={{ margin: '0 0 12px' }}>
                Par la présente, je vous informe de ma décision de démissionner de mon poste de{' '}
                <b>{emp.role || 'collaborateur ou collaboratrice'}</b> au sein de{' '}
                <b>{soc?.nom || SOC.nom}</b>, dans le respect du délai de congé prévu par mon
                contrat et par le Code des obligations (art. 335 ss CO).
              </p>
              <p style={{ margin: '0 0 12px' }}>
                Mon dernier jour de travail sera ainsi fixé au <b>{dd(dernier)}</b>.
              </p>
              {motif && motif.trim() && <p style={{ margin: '0 0 12px' }}>{motif}</p>}
              <p style={{ margin: '0 0 12px' }}>
                Je vous remercie pour la confiance que vous m'avez accordée ainsi que pour notre
                collaboration durant cette période.
              </p>
              <p style={{ margin: '0 0 12px' }}>
                Je vous prie d'agréer, Madame, Monsieur, mes salutations distinguées.
              </p>
            </div>
            <div style={{ marginTop: 26, display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ textAlign: 'center', minWidth: 200 }}>
                <div style={{ fontSize: 9, color: '#8a909c', marginBottom: 4, textAlign: 'left' }}>
                  Signature de l'employé·e :
                </div>
                {sign || emp.signature ? (
                  <img
                    src={sign || emp.signature}
                    alt="signature"
                    style={{ height: 64, objectFit: 'contain', display: 'block' }}
                  />
                ) : (
                  <div style={{ height: 64 }} />
                )}
                <div
                  style={{
                    borderTop: '1px solid #8a909c',
                    paddingTop: 5,
                    fontSize: 10.5,
                    fontWeight: 700,
                  }}
                >
                  {emp.nom}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: 30,
                paddingTop: 12,
                borderTop: '1px solid #dde1e6',
                fontSize: 8.5,
                color: '#a0a6b0',
                lineHeight: 1.6,
              }}
            >
              Document signé électroniquement via SwissTech le {dateEmis}. La signature manuscrite
              reste recommandée pour l'exemplaire officiel remis à l'employeur.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Card style={{ marginBottom: 11 }}>
        <Bar
          icon="✍️"
          title="Ma lettre de démission"
          color={C.purple}
          sub="Rédige, signe et remets ta démission"
        />
        <div style={{ padding: 13 }}>
          <div
            style={{
              background: C.tealL,
              borderRadius: 9,
              padding: '10px 12px',
              marginBottom: 11,
              fontSize: 9,
              color: C.grey,
              lineHeight: 1.55,
            }}
          >
            ℹ️ Cette lettre est <b>personnelle</b>. Elle reprend ton nom, ton poste et l'entreprise.
            Le <b>dernier jour</b> se calcule selon ton <b>délai</b>
            {paysCH ? ' (loi suisse : fin de mois)' : ''} — modifiable.
          </div>
          <div
            style={{
              fontSize: 8.5,
              color: C.muted,
              marginBottom: 5,
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            Délai de résiliation
          </div>
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              alignItems: 'center',
              marginBottom: 9,
            }}
          >
            {[1, 2, 3].map((m) => (
              <div
                key={m}
                onClick={() => setDelaiMois(m)}
                style={{
                  padding: '7px 13px',
                  borderRadius: 9,
                  fontSize: 10.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                  border: '1px solid ' + (delaiMois === m ? C.navy : C.border),
                  background: delaiMois === m ? C.navy : C.white,
                  color: delaiMois === m ? '#fff' : C.grey,
                }}
              >
                {m} mois
              </div>
            ))}
            <input
              type="number"
              min="1"
              value={delaiMois}
              onChange={(e) => setDelaiMois(Math.max(1, +e.target.value || 1))}
              style={{
                width: 64,
                border: '1px solid ' + C.border,
                borderRadius: 9,
                padding: '8px',
                fontSize: 11,
                textAlign: 'center',
              }}
            />
          </div>
          {dateCalc && (
            <div
              style={{
                background: C.greenL,
                border: '1px solid #bfe6cf',
                borderRadius: 9,
                padding: '10px 12px',
                marginBottom: 11,
              }}
            >
              <div style={{ fontSize: 9, fontWeight: 800, color: C.green }}>
                📅 Dernier jour calculé{paysCH ? ' · fin de mois (loi suisse)' : ''}
              </div>
              <div style={{ fontSize: 15, fontWeight: 900, color: C.ink, margin: '3px 0 7px' }}>
                {dd(dateCalc)}
              </div>
              <Btn
                label="Utiliser cette date"
                sm
                color={C.green}
                onClick={() => setDernier(dateCalc)}
              />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginBottom: 9 }}>
            <div>
              <div style={{ fontSize: 8.5, color: C.muted, marginBottom: 3, fontWeight: 700 }}>
                Date de la lettre
              </div>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={inp}
              />
            </div>
            <div>
              <div style={{ fontSize: 8.5, color: C.muted, marginBottom: 3, fontWeight: 700 }}>
                Dernier jour de travail
              </div>
              <input
                type="date"
                value={dernier}
                onChange={(e) => setDernier(e.target.value)}
                style={inp}
              />
            </div>
          </div>
          {dernier && dateCalc && dernier < dateCalc && (
            <div
              style={{
                background: C.amberL,
                border: '1px solid #f0d9bd',
                borderRadius: 9,
                padding: '10px 12px',
                marginBottom: 10,
              }}
            >
              <div style={{ fontSize: 9.5, fontWeight: 800, color: C.amber }}>
                ⚠ Attention à ton délai
              </div>
              <div style={{ fontSize: 9, color: C.grey, lineHeight: 1.5, marginTop: 4 }}>
                Ta date ({dd(dernier)}) est plus tôt que la date valable ({dd(dateCalc)}) selon ton
                délai de {delaiMois} mois. Tu peux la garder, mais elle risque de ne pas être
                valable.
              </div>
              <div style={{ marginTop: 6 }}>
                <Btn
                  label={'Corriger au ' + dd(dateCalc)}
                  sm
                  color={C.amber}
                  onClick={() => setDernier(dateCalc)}
                />
              </div>
            </div>
          )}
          <div style={{ marginBottom: 11 }}>
            <div style={{ fontSize: 8.5, color: C.muted, marginBottom: 3, fontWeight: 700 }}>
              Message / motif (optionnel)
            </div>
            <textarea
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Ex. nouveau projet professionnel, raisons personnelles… (facultatif)"
              style={{ ...inp, minHeight: 64, fontFamily: 'inherit', resize: 'vertical' }}
            />
            {/* Le motif figure tel quel sur la lettre : le correcteur local,
                offert sous chaque autre zone de texte, l'est aussi ici. */}
            <BoutonCorriger value={motif} onChange={(e) => setMotif(e.target.value)} />
          </div>
          <div style={{ fontSize: 8.5, color: C.muted, marginBottom: 5, fontWeight: 700 }}>
            SIGNATURE ÉLECTRONIQUE
          </div>
          <SignaturePad onSign={setSign} />
          <div style={{ marginTop: 14 }}>
            <Btn
              label="👁 Voir / générer ma lettre"
              color={C.purple}
              full
              disabled={!dernier || !sign}
              onClick={() => setStep('letter')}
            />
            {boutonRemise}
          </div>
          {(!dernier || !sign) && (
            <div style={{ fontSize: 8.5, color: C.amber, marginTop: 7, textAlign: 'center' }}>
              {!dernier ? 'Choisis ton dernier jour de travail. ' : ''}
              {!sign ? 'Ajoute ta signature.' : ''}
            </div>
          )}
        </div>
      </Card>
    </>
  );
}
